import { type FormEvent, type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';

import { busyButton } from '../../components/busy.ts';
import { InstallIcon } from '../../components/icons.tsx';
import { copyText, shareOrCopy } from '../../game/share.ts';
import {
	createNest,
	fetchInvite,
	type InviteView,
	joinNest,
	NEST_CODE,
	NestError,
	type NestSummary,
	type NestView,
	normaliseCode,
} from '../../nests/api.ts';
import { inLine, inviteLink, inviteText, peopleLine } from '../../nests/model.ts';
import { claimTypedCode, prepareCarry } from '../../pwa/install.ts';
import { Sheet, SwitchRow } from '../../ui/index.ts';
import { DISPLAY_NAME_MAX, Field, FormError, NEST_NAME_MAX, nameProblem, nestErrorField, nestErrorLine, TickOn } from './parts.tsx';

/** One of the nest's sheets (one at a time; each is a ui/Sheet, and one handing over to the next keeps the one history entry). */
export type NestSheetKind = 'menu' | 'invite' | 'start' | 'join' | 'move' | 'bring';

/** A form's error, and the field it's about (null: the whole form, e.g. the connection). */
type FormErr<F extends string> = { text: string; field: F | null } | null;

/** After a failed submit, focus goes to the field the error is about (A11Y-8). */
function useFocusOnError<F extends string>(error: FormErr<F>, refs: Partial<Record<F, RefObject<HTMLInputElement | null>>>): void {
	const latest = useRef(refs);
	latest.current = refs;
	useEffect(() => {
		if (error?.field) latest.current[error.field]?.current?.focus({ preventScroll: true });
	}, [error]);
}

/** A submit that's busy keeps its focus (UX-019): the form's own guard does the rest. */
const busySubmit = (busy: boolean) => busyButton(busy, () => {});

// ------------------------------------------------------------------------- Start a nest

export function StartSheet({
	todayKey,
	myName,
	onClose,
	onStarted,
}: {
	todayKey: string;
	myName: string;
	onClose: () => void;
	onStarted: (nest: NestView, myName: string) => void;
}) {
	const [name, setName] = useState('');
	const [me, setMe] = useState(myName);
	const [error, setError] = useState<FormErr<'nest' | 'you'>>(null);
	const [busy, setBusy] = useState(false);
	const nestRef = useRef<HTMLInputElement>(null);
	const meRef = useRef<HTMLInputElement>(null);
	useEffect(() => nestRef.current?.focus({ preventScroll: true }), []);
	useFocusOnError(error, { nest: nestRef, you: meRef });

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		if (busy) return;
		const nestProblem = nameProblem(name, NEST_NAME_MAX, 'nest');
		if (nestProblem) return setError({ text: nestProblem, field: 'nest' });
		const meProblem = nameProblem(me, DISPLAY_NAME_MAX, 'you');
		if (meProblem) return setError({ text: meProblem, field: 'you' });
		setBusy(true);
		setError(null);
		try {
			const nest = await createNest({ name: name.trim(), displayName: me.trim(), date: todayKey });
			onStarted(nest, me.trim());
		} catch (err) {
			setError({ text: nestErrorLine(err, 'start'), field: nestErrorField(err) });
		} finally {
			setBusy(false);
		}
	};

	const on = (f: 'nest' | 'you') => (error?.field === f ? error.text : null);
	return (
		<Sheet open title="Start a nest" onClose={onClose} className="nest-sheet" testId="nest-start">
			<form onSubmit={(e) => void submit(e)} noValidate>
				<Field id="nest-name" label="Name of the nest" value={name} onChange={setName} max={NEST_NAME_MAX} enterKeyHint="next" inputRef={nestRef} error={on('nest')} />
				<Field id="nest-me" label="Your name" value={me} onChange={setMe} max={DISPLAY_NAME_MAX} autoComplete="given-name" enterKeyHint="done" inputRef={meRef} error={on('you')} />
				<FormError text={error && !error.field ? error.text : null} />
				<button type="submit" className="btn lg primary sh-pill" {...busySubmit(busy)}>
					Start the nest
				</button>
			</form>
		</Sheet>
	);
}

// ---------------------------------------------------------------------- Join with a code

export function JoinSheet({
	todayKey,
	myName,
	onClose,
	onJoined,
	onAlreadyIn,
	onStart,
}: {
	todayKey: string;
	myName: string;
	onClose: () => void;
	onJoined: (nest: NestView, myName: string) => void;
	onAlreadyIn: () => void;
	/** A full nest: start one of your own instead. */
	onStart: () => void;
}) {
	const [code, setCode] = useState('');
	const [found, setFound] = useState<InviteView | null>(null);
	const [me, setMe] = useState(myName);
	const [error, setError] = useState<FormErr<'code' | 'you'>>(null);
	const [busy, setBusy] = useState(false);
	const codeRef = useRef<HTMLInputElement>(null);
	const nameRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		(found ? nameRef : codeRef).current?.focus({ preventScroll: true });
	}, [found]);
	useFocusOnError(error, { code: codeRef, you: nameRef });

	const find = async (e: FormEvent) => {
		e.preventDefault();
		if (busy) return;
		const c = normaliseCode(code);
		if (!NEST_CODE.test(c)) return setError({ text: 'A code is 6 letters or numbers.', field: 'code' });
		setBusy(true);
		setError(null);
		try {
			setFound(await fetchInvite(c, null));
		} catch (err) {
			const kind = err instanceof NestError ? err.kind : null;
			setError({ text: nestErrorLine(err, 'join'), field: kind === 'not-found' || kind === 'bad-request' ? 'code' : null });
		} finally {
			setBusy(false);
		}
	};

	const join = async (e: FormEvent) => {
		e.preventDefault();
		if (busy || !found) return;
		const problem = nameProblem(me, DISPLAY_NAME_MAX, 'you');
		if (problem) return setError({ text: problem, field: 'you' });
		setBusy(true);
		setError(null);
		try {
			onJoined(await joinNest({ code: found.code, displayName: me.trim(), date: todayKey }), me.trim());
		} catch (err) {
			setError({ text: nestErrorLine(err, 'join'), field: nestErrorField(err) === 'you' ? 'you' : null });
		} finally {
			setBusy(false);
		}
	};

	const on = (f: 'code' | 'you') => (error?.field === f ? error.text : null);
	const formError = <FormError text={error && !error.field ? error.text : null} />;
	if (found) {
		const who = inLine(found.members, found.memberCount, found.name);
		return (
			<Sheet open title={`Join ${found.name}`} onClose={onClose} className="nest-sheet" testId="nest-join">
				{found.joined ? (
					<>
						<p className="sh-s">You’re already in {found.name}.</p>
						<button type="button" className="btn lg primary sh-pill" onClick={onAlreadyIn}>
							Open {found.name}
						</button>
					</>
				) : found.full ? (
					<>
						<p className="sh-s">{found.name} is full: a nest holds up to 30 people.</p>
						<button type="button" className="btn lg primary sh-pill" onClick={onStart}>
							Start a nest
						</button>
					</>
				) : (
					<form onSubmit={(e) => void join(e)} noValidate>
						{who && <p className="sh-s">{who}</p>}
						<Field id="nest-join-me" label="Your name" value={me} onChange={setMe} max={DISPLAY_NAME_MAX} autoComplete="given-name" enterKeyHint="go" inputRef={nameRef} error={on('you')} />
						{formError}
						<button type="submit" className="btn lg primary sh-pill" {...busySubmit(busy)}>
							Join {found.name}
						</button>
					</form>
				)}
			</Sheet>
		);
	}
	return (
		<Sheet open title="Join with a code" onClose={onClose} className="nest-sheet" testId="nest-join">
			<form onSubmit={(e) => void find(e)} noValidate>
				<p className="sh-s">Ask anyone in the nest for its code.</p>
				<Field id="nest-code" label="Code" value={code} onChange={setCode} max={6} code enterKeyHint="go" inputRef={codeRef} error={on('code')} />
				{formError}
				<button type="submit" className="btn lg primary sh-pill" {...busySubmit(busy)}>
					Find the nest
				</button>
			</form>
		</Sheet>
	);
}

// ------------------------------------------------------------------------- Invite people

/** How long "Link copied" stands in for Copy link. */
const COPIED_MS = 2000;

/**
 * Share the link, or copy it, or read out the code. A copy confirms in place, as the poster
 * and the install guide do: Copy link reads "Link copied" for a moment, and the sheet's own
 * status line says it (a slip above the tab bar would sit on Share the link: QA2-4). A
 * failure stays in that line, with what to do.
 */
export function InviteSheet({ nest, onClose }: { nest: NestView; onClose: () => void }) {
	const [copied, setCopied] = useState(false);
	const [problem, setProblem] = useState<string | null>(null);
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	useEffect(() => () => clearTimeout(timer.current), []);
	const confirm = (ok: boolean, failed: string) => {
		clearTimeout(timer.current);
		setCopied(ok);
		setProblem(ok ? null : failed);
		if (ok) timer.current = setTimeout(() => setCopied(false), COPIED_MS);
	};
	const share = async () => {
		const how = await shareOrCopy(inviteText(nest));
		if (how === 'clipboard') confirm(true, '');
		else if (how === 'failed') confirm(false, 'Couldn’t share from this browser. Copy the link instead.');
	};
	const copy = async () => confirm(await copyText(inviteLink(nest)), 'Couldn’t copy the link. Read out the code instead.');
	return (
		<Sheet open title={`Invite people to ${nest.name}`} onClose={onClose} className="nest-sheet" testId="nest-invite">
			<p className="sh-s">Anyone with the link can join. They don’t need to install anything.</p>
			<button type="button" className="btn lg primary sh-pill" onClick={() => void share()}>
				<InstallIcon.share />
				Share the link
			</button>
			<p className="inv-alt">
				<button type="button" className="tlink" onClick={() => void copy()}>
					{copied ? 'Link copied' : 'Copy link'}
				</button>
				<span>
					or read out the code <b className="inv-code">{nest.code}</b>
				</span>
			</p>
			<p className={problem ? 'field-error' : 'sr-only'} role="status">
				{problem ?? (copied ? 'Link copied' : '')}
			</p>
		</Sheet>
	);
}

// ----------------------------------------------------------------------- The nest sheet

/**
 * The nest's one sheet (its name is the title's switcher): your nests when you have more
 * than one, then Invite, Start and Join; below the rule, set quietly, Mute, this device's
 * move to or from the home-screen app (a chore, not a nest action: VH-19), and Leave.
 * Leave asks first, in the sheet (never `confirm()`).
 */
export function NestMenuSheet({
	nest,
	nests,
	onClose,
	onSwitch,
	onPick,
	onMute,
	onLeave,
	canMove = false,
	canBring = false,
}: {
	nest: NestView;
	nests: readonly NestSummary[];
	onClose: () => void;
	onSwitch: (id: string) => void;
	onPick: (kind: Exclude<NestSheetKind, 'menu'>) => void;
	onMute: (muted: boolean) => void;
	onLeave: () => Promise<string | null>;
	/** An iPhone in Safari: offer the code that moves this device's nests into the home-screen app. */
	canMove?: boolean;
	/** The iPhone's home-screen app: take that code. */
	canBring?: boolean;
}) {
	const [confirm, setConfirm] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const many = nests.length > 1;

	const row = (key: string, label: ReactNode, sub: string | null, onClick: () => void, current = false) => (
		<li key={key}>
			<button type="button" className="mn-row nest-row" onClick={onClick} aria-current={current || undefined}>
				<span>
					{label}
					{sub && <small>{sub}</small>}
				</span>
				{current ? (
					<TickOn />
				) : (
					<span className="mn-chev">
						<InstallIcon.fwd />
					</span>
				)}
			</button>
		</li>
	);

	if (confirm) {
		const leave = async () => {
			if (busy) return;
			setBusy(true);
			setError(null);
			const problem = await onLeave();
			setBusy(false);
			if (problem) setError(problem);
		};
		return (
			<Sheet open title={`Leave ${nest.name}?`} onClose={onClose} className="nest-sheet" testId="nest-menu">
				<p className="sh-s">This removes your name and activity from {nest.name}. You can join again with the link.</p>
				<FormError text={error} />
				<div className="sh-acts">
					<button type="button" className="btn lg danger sh-pill nest-leave" {...busyButton(busy, () => void leave())}>
						Leave {nest.name}
					</button>
					<button type="button" className="tlink" onClick={() => setConfirm(false)}>
						Stay
					</button>
				</div>
			</Sheet>
		);
	}

	const device = canMove
		? row('move', 'Move to the home screen app', 'A code that brings your nests across', () => onPick('move'))
		: canBring
			? row('bring', 'Bring your nests from Safari', 'With the code Safari shows you', () => onPick('bring'))
			: null;
	return (
		<Sheet open title={many ? 'Your nests' : nest.name} onClose={onClose} className="nest-sheet" testId="nest-menu">
			{many && (
				<ul className="mn-list mn-block" aria-label="Your nests">
					{nests.map((n) => row(n.id, n.name, peopleLine(n.memberCount), () => onSwitch(n.id), n.id === nest.id))}
				</ul>
			)}
			<ul className="mn-list mn-block">
				{row('invite', `Invite people to ${nest.name}`, null, () => onPick('invite'))}
				{row('start', 'Start a nest', null, () => onPick('start'))}
				{row('join', 'Join with a code', null, () => onPick('join'))}
			</ul>
			<div className="mn-block nest-quiet">
				<SwitchRow label={`Mute ${nest.name}`} sub="No notifications from this nest" checked={nest.me.muted} onToggle={() => onMute(!nest.me.muted)} />
				{device && <ul className="mn-list nest-device">{device}</ul>}
				<button type="button" className="tlink nest-leave-link" onClick={() => setConfirm(true)}>
					Leave {nest.name}
				</button>
			</div>
		</Sheet>
	);
}

// ------------------------------------------------- Moving to the home-screen app (iPhone)

/**
 * In Safari on an iPhone: a carry code for the home-screen app, which has storage of its
 * own and so starts without this device's nests (docs/NESTS.md, "Moving to the home
 * screen"). The app takes it in "Bring your nests here".
 */
export function MoveSheet({ onClose }: { onClose: () => void }) {
	const [code, setCode] = useState<string | null>(null);
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		let live = true;
		setFailed(false);
		void prepareCarry().then((c) => {
			if (!live) return;
			if (c) setCode(c.toUpperCase());
			else setFailed(true);
		});
		return () => {
			live = false;
		};
	}, [attempt]);
	return (
		<Sheet open title="Move to the home screen app" onClose={onClose} className="nest-sheet" testId="nest-move">
			<p className="sh-s">Open Sparrow from your home screen, go to Nest, tap Bring your nests here, and type this code. It works for 30 minutes.</p>
			{code ? (
				// Read letter by letter (a paragraph can't carry a name, A11Y-20).
				<p className="carry-code">
					<span aria-hidden="true">{code}</span>
					<span className="sr-only">Code: {code.split('').join(' ')}</span>
				</p>
			) : failed ? (
				<>
					<FormError text="Couldn’t make a code. Check your connection and try again." />
					<button type="button" className="btn lg primary sh-pill" onClick={() => setAttempt((n) => n + 1)}>
						Try again
					</button>
				</>
			) : (
				<p className="sr-only" role="status">
					Making a code
				</p>
			)}
		</Sheet>
	);
}

/** In the home-screen app: type the code from Safari, and this app becomes that device, nests and all. */
export function BringSheet({ onClose, onBrought }: { onClose: () => void; onBrought: () => void }) {
	const [code, setCode] = useState('');
	const [error, setError] = useState<FormErr<'code'>>(null);
	const [busy, setBusy] = useState(false);
	const ref = useRef<HTMLInputElement>(null);
	useEffect(() => ref.current?.focus({ preventScroll: true }), []);
	useFocusOnError(error, { code: ref });
	const bring = async (e: FormEvent) => {
		e.preventDefault();
		if (busy) return;
		const c = normaliseCode(code);
		if (!NEST_CODE.test(c)) return setError({ text: 'A code is 6 letters or numbers.', field: 'code' });
		setBusy(true);
		setError(null);
		const ok = await claimTypedCode(c);
		setBusy(false);
		if (ok) onBrought();
		else setError({ text: 'That code didn’t work. Codes last 30 minutes, so get a new one in Safari.', field: 'code' });
	};
	return (
		<Sheet open title="Bring your nests here" onClose={onClose} className="nest-sheet" testId="nest-bring">
			<form onSubmit={(e) => void bring(e)} noValidate>
				<p className="sh-s">In Safari, open Sparrow’s Nest tab, tap your nest’s name, then Move to the home screen app. Type the code it shows.</p>
				<Field id="nest-bring-code" label="Code" value={code} onChange={setCode} max={6} code enterKeyHint="go" inputRef={ref} error={error?.text ?? null} />
				<button type="submit" className="btn lg primary sh-pill" {...busySubmit(busy)}>
					Bring them here
				</button>
			</form>
		</Sheet>
	);
}
